<template>
  <fieldset>
    <div class="row align-items-center mb-10 custom-location-buttons">
      <div class="col-sm-3 picker-wrapper">
        <div
          v-if="mapPinIndex"
          class="map-pin-index"
        >
          {{ mapPinIndex }}
        </div>
        <img
          v-if="mapPinShow && mapPinImg"
          :src="mapPinImg"
          :class="{ 'cursor-pointer': makeMarkerClickable }"
          @click="emitMarkerClickable(parentId)"
        >
      </div>

      <div class="col-sm-8 button-group">
        <el-tooltip
          class="item"
          effect="dark"
          placement="top"
          popper-class="custom-tooltip"
        >
          <template #content>
            Home Address
          </template>
          <button
            :id="`btnHome-${parentId}`"
            type="button"
            class="btn mr-10"
            :class="{
              filled: isCoordinateChanged(customerInfo.homeCoordinatesChanges),
              active: selectedButton === 'home',
            }"
            :disabled="disabled"
            @click="getHomeAddress"
          >
            <i class="fa fa-home" />
          </button>
        </el-tooltip>

        <el-tooltip
          class="item"
          effect="dark"
          placement="top"
          popper-class="custom-tooltip"
        >
          <template #content>
            Last Drop Off Location Address
          </template>
          <button
            :id="`btnLastRide-${parentId}`"
            type="button"
            class="btn"
            :class="{ active: selectedButton === 'last' }"
            :disabled="disabled"
            @click="getLastAddress"
          >
            L
          </button>
        </el-tooltip>

        <div class="address-list-container">
          <dropdown-list
            v-model="customLocationIndex"
            :disabled="disabled"
          >
            <template #additional-options-prefix>
              <el-option
                :key="0"
                label="None Selected"
                :value="0"
                disabled
              >
                None Selected
              </el-option>
              <el-option
                v-for="(customLocation, index) in customLocationList"
                :key="index + 1"
                :value="index + 1"
                :label="`${customLocation.order}) ${customLocation.name || 'Unnamed custom location'}`"
              >
                {{ `${customLocation.order}) ${customLocation.name || 'Unnamed custom location'}` }}
              </el-option>
            </template>
          </dropdown-list>
        </div>
      </div>
    </div>

    <div
      v-for="(value, index) in ['a', 'b', 'c']"
      :key="index"
      class="row custom-location-buttons"
    >
      <div class="offset-sm-3 col-sm-8 button-group">
        <button
          :id="`btnCustom${index + 3}-${parentId}`"
          type="button"
          class="btn full-width"
          :class="{
            active: selectedButton === `custom_${value}`,
            filled: customerInfo[`${value}Location`] && isCoordinateChanged(customerInfo[`${value}LocationCoordinatesChanges`]),
          }"
          :disabled="disabled"
          @click="getCustomLocation(index + 1)"
        >
          {{ index + 3 }}: {{ getCustomLocationButtonText(value) }}
        </button>
      </div>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { ref, Ref, computed, watch } from 'vue';
import _ from 'lodash';

import { useCustomer } from '@/use/customer';
import { useAddress } from '@/use/address';
import { sendGet } from '@/Utils/api';
import { LocationDataType, CustomLocationType } from '@/models/address';

const props = defineProps({
  parentId: {
    type: String,
    default: 'form',
  },

  makeMarkerClickable: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  mapPinShow: {
    type: Boolean,
    default: false,
  },

  mapPinType: {
    type: String,
    default: '',
  },

  mapPinIndex: {
    type: Number,
    default: 0,
  },

  lastLocationUrl: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['load-address', 'change', 'show-location-name-dialog', 'update-map-from-marker-click']);

const { customerInfo } = useCustomer();
const { getFormattedAddress, getAddressObjectFromString } = useAddress();

const emitMarkerClickable = (markerType: string) => {
  emit('update-map-from-marker-click', markerType);
};

const customLocationIndex = ref(0);
const customLocationList: Ref<CustomLocationType[]> = ref([]);
const selectedButton = ref('');

const mapPinImg = computed(() => {
  if (props.mapPinType === 'pickup') {
    return '/static/img/pickup_marker.png';
  }

  if (props.mapPinType === 'dropoff') {
    return '/static/img/dropoff_marker.png';
  }

  if (props.mapPinType === 'multi_stop') {
    return '/static/img/multi_stops_marker.png';
  }

  return '';
});

const getHomeAddress = () => {
  selectedButton.value = 'home';
  customLocationIndex.value = 0;

  let address = makeAddressObject(customerInfo.value);
  address.name = customerInfo.value?.customHomeName || 'Home';

  emit('change', address);
};

const getLastAddress = async () => {
  let address: any = makeAddressObject({});
  selectedButton.value = 'last';
  customLocationIndex.value = 0;

  emit('load-address', true, 'Loading last ride address...');

  try {
    const response = await sendGet(props.lastLocationUrl || `callers/${customerInfo.value.id}/lastLocation`);

    if (!response.data.streetAddress) {
      selectedButton.value = '';
      return;
    }

    address = makeAddressObject({
      ...response.data,
      latitude: Number(response.data.lat),
      longitude: Number(response.data.lng),
    });
  } catch (error) {
    selectedButton.value = '';
  } finally {
    emit('load-address', false, 'Loading last ride address...');
    emit('change', address);
  }
};

const getCustomLocationButtonText = (loc: string) => {
  if (customerInfo.value[loc + 'Location']) {
    const fullAddress = customerInfo.value[loc + 'Location'];

    return customerInfo.value[loc + 'Name'] ? `${customerInfo.value[loc + 'Name']} - ${fullAddress}` : fullAddress;
  } else {
    return 'None';
  }
};

// category => 0: CustomLocation 3#, 4#, 5#,  category => 1: custom location 1* ~
const getCustomLocation = (index: number, category: number = 0) => {
  let newAddress: CustomLocationType = makeAddressObject({});
  let locationOrderIndex = index;
  let needToAddLocationName = false;

  if (category === 0) {
    const strInds = ['a', 'b', 'c'] as string[];
    const loc = strInds[index - 1] as string;
    customLocationIndex.value = 0;
    if (customerInfo.value[loc + 'Location']) {
      const address = getAddressObjectFromString(customerInfo.value[loc + 'Location']) as LocationDataType;
      newAddress = makeAddressObject({
        ...address,
        latitude: customerInfo.value[loc + 'Lat'],
        longitude: customerInfo.value[loc + 'Long'],
        name: customerInfo.value[loc + 'Name'],
      });

      selectedButton.value = 'custom_' + loc;

      if (!customerInfo.value[loc + 'Name']) {
        // aName = null
        needToAddLocationName = true;
      } else if (customerInfo.value[loc + 'Name'] === customerInfo.value[loc + 'Location']) {
        // aLocation === aName
        needToAddLocationName = true;
      }
    }
  } else {
    if (customLocationList.value?.length > 0) {
      const address = customLocationList.value[index - 1] as CustomLocationType;
      if (address) {
        newAddress = makeAddressObject(address);
        const fullAddress = getFormattedAddress(newAddress);
        if (!address.name || address.name.includes(fullAddress)) {
          needToAddLocationName = true;
        }
      }
    }
  }

  emit('change', newAddress);

  if (needToAddLocationName) {
    emit('show-location-name-dialog', locationOrderIndex);
  }
};

const makeAddressObject = (address: any) => ({
  id: address.id || null, // only custom locations *1-*8 has an id because they are in a separate table callerAddresses
  name: address.name || '',
  streetAddress: address.streetAddress || '',
  streetAddress1: '',
  streetAddress2: address.streetAddress2 || '',
  city: address.city || '',
  state: address.state || '',
  zip: address.zip || '',
  country: address.country || '',
  latitude: address.latitude || null,
  longitude: address.longitude || null,
  googlePlaceId: address?.googlePlaceId || '',
});

const isCoordinateChanged = (changes: any) => {
  if (!changes) {
    return false;
  }

  if (changes.lastLat || changes.lastLong) {
    return true;
  }

  return false;
};

watch(
  customerInfo,
  (value: any) => {
    selectedButton.value = '';
    customLocationIndex.value = 0;
    if (value?.id) {
      customLocationList.value = _.cloneDeep(value.customAddresses || []);
      customLocationList.value = _.orderBy(customLocationList.value, ['order']);
    } else {
      customLocationList.value = [];
    }
  },
  {
    immediate: true,
  },
);

watch(customLocationIndex, (value: number) => {
  if (value) {
    getCustomLocation(value, 1);
  }
});
</script>

<style lang="scss">
.custom-location-buttons {
  margin-bottom: 5px !important;
  &:first-child {
    margin-bottom: 10px !important;
  }
  &:last-child {
    margin-bottom: 20px !important;
  }
  .button-group {
    display: flex;
    align-items: center;
    .location {
      margin-top: -4px;
      font-size: 27px;
    }

    button {
      height: 30px;
      width: 30px !important;
      border-radius: 50%;
      padding: 0 !important;
      font-size: 16px !important;
      background: transparent;
      color: #66615b;
      border-width: 2px;
      border-style: solid;
      &.active {
        border-color: #41b883;
        background-color: #41b883 !important;
        color: #fff !important;
      }
      &.filled {
        border-color: #66615b;
        background-color: #66615b !important;
        color: #fff !important;
        &.active {
          border-color: #41b883;
          background-color: #41b883 !important;
          color: #fff !important;
        }
      }
      &.full-width {
        width: 100% !important;
        border-radius: 30px;
        text-align: left;
        padding: 0 15px !important;
        overflow: hidden;
        white-space: nowrap;
        display: block;
        text-overflow: ellipsis;
        font-size: 12px !important;
      }
    }

    .address-list-container {
      flex: 1;
      padding-left: 10px;
    }
  }
  .picker-wrapper {
    text-align: right;

    @media (max-width: 576px) {
      text-align: left;
      margin-bottom: 5px;
    }
  }
  .map-pin-index {
    position: absolute;
    right: 15px;
    font-size: 16px;
    font-weight: bold;
    width: 40px;
    text-align: center;
    top: 3px;
    color: #000;
  }
}
</style>
